/**
 * SCRUM-977 — the per-column filter option catalogues, transcribed VERBATIM from
 * Ryan's `app-shell 7b.html` `COLUMN_FILTERS` (L10777-10889): exact labels and
 * exact values. This is the single source the header menu renders and the tests
 * assert against, so "matches Ryan exactly" is verifiable in one place.
 *
 * `branch` / `region` are the SCRUM-956 geography columns Ryan never had; their
 * options are DERIVED from the loaded customers at render time (tenant data), so
 * they are not listed here — only Ryan's fixed catalogues are.
 */
import type { ColumnFilters } from './ledger-filter';

/** One selectable option: the label shown and the filter value it sets. */
export interface ColumnFilterOption<K extends keyof ColumnFilters> {
  label: string;
  value: NonNullable<ColumnFilters[K]>;
}

export const NAME_OPTIONS: ColumnFilterOption<'name'>[] = [
  { label: 'Starts with A – E', value: { from: 'A', to: 'F' } },
  { label: 'Starts with F – J', value: { from: 'F', to: 'K' } },
  { label: 'Starts with K – O', value: { from: 'K', to: 'P' } },
  { label: 'Starts with P – T', value: { from: 'P', to: 'U' } },
  { label: 'Starts with U – Z', value: { from: 'U', to: '~' } },
];

export const TYPE_OPTIONS: ColumnFilterOption<'type'>[] = [
  { label: 'Personal', value: 'Personal' },
  { label: 'Business', value: 'Business' },
];

/** Ryan `age` = TENURE column (months). */
export const TENURE_OPTIONS: ColumnFilterOption<'tenure'>[] = [
  { label: '< 6 months', value: { min: 0, max: 6 } },
  { label: '6 – 12 months', value: { min: 6, max: 12 } },
  { label: '12 – 36 months', value: { min: 12, max: 36 } },
  { label: '36 – 60 months', value: { min: 36, max: 60 } },
  { label: '> 60 months', value: { min: 60, max: 9999 } },
];

export const HOLDER_AGE_OPTIONS: ColumnFilterOption<'holderAge'>[] = [
  { label: 'Under 30', value: { min: 0, max: 30 } },
  { label: '30 – 49', value: { min: 30, max: 50 } },
  { label: '50 – 64', value: { min: 50, max: 65 } },
  { label: '65 +', value: { min: 65, max: 999 } },
];

export const BALANCE_OPTIONS: ColumnFilterOption<'balance'>[] = [
  { label: '< $10K', value: { min: 0, max: 10000 } },
  { label: '$10K – $50K', value: { min: 10000, max: 50000 } },
  { label: '$50K – $200K', value: { min: 50000, max: 200000 } },
  { label: '> $200K', value: { min: 200000, max: 9999999 } },
];

export const HOLDINGS_OPTIONS: ColumnFilterOption<'holdings'>[] = [
  { label: '1 product', value: { min: 1, max: 1 } },
  { label: '2 products', value: { min: 2, max: 2 } },
  { label: '3 products', value: { min: 3, max: 3 } },
  { label: '4 products', value: { min: 4, max: 4 } },
  { label: '5+ products', value: { min: 5, max: 99 } },
];

export const SIGNALS_OPTIONS: ColumnFilterOption<'signals'>[] = [
  { label: 'Has any signal', value: 'has' },
  { label: 'No active signals', value: 'none' },
  { label: 'Dormant Account', value: 'dormant' },
  { label: 'Deposit Flight Risk', value: 'flight' },
  { label: 'SMB Growth', value: 'smb' },
  { label: 'Relationship Gap', value: 'relgap' },
];

export const MATCH_OPTIONS: ColumnFilterOption<'match'>[] = [
  { label: 'Perfect match (100%)', value: { min: 1.0, strict: true } },
  { label: 'High match (≥ 80%)', value: { min: 0.8 } },
  { label: 'Partial match (50 – 79%)', value: { min: 0.5, max: 0.8 } },
  { label: 'Low match (< 50%)', value: { min: 0, max: 0.5 } },
];

export const LAST_OPTIONS: ColumnFilterOption<'last'>[] = [
  { label: 'Contacted < 14 days', value: { max: 14 } },
  { label: 'Contacted < 30 days', value: { max: 30 } },
  { label: 'Never contacted', value: 'never' },
];
